/**
 * Health Check Page
 * Public endpoint that checks system connectivity and displays status
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, CircularProgress, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';
// REACT_APP_MONITORING_URL is deliberately no longer read here — see the removed
// observability card below. Grafana is not probed from the browser.

// Service check configuration
const SERVICES = [
  {
    name: 'CARO API',
    key: 'api',
    url: `${API_URL}/api/health`,
    description: 'Core API services'
  },
  // REMOVED: the "Monitoring Service" / "Observability" card.
  // Spec: specs/20260805-api-observability/ §2.7, §4.10.
  //
  // It originally probed `${MONITORING_URL}/health` on the retired caro-monitoring-service.
  // Re-pointing it at Grafana on VM5 did not work either, for a structural reason: this page
  // fetches cross-origin from portal.caro.care, and Grafana sends no
  // `Access-Control-Allow-Origin`. The browser blocks the response, the fetch throws, and the
  // card renders red with a plausible-looking ~35ms response time. It was reporting on the
  // browser's CORS policy, not on Grafana.
  //
  // Giving Grafana CORS headers would open its API to browser JavaScript, which is exactly
  // what §4.10 avoids. The right fix is a same-origin, authenticated check through the
  // CARO-Api proxy (`routes/dynamic/observability.js`) — that arrives in Phase 4, and the
  // card can come back then.
  //
  // Two honest cards beat three, one of which is always red and means nothing.
  {
    name: 'Portal Frontend',
    key: 'portal',
    url: null, // Self-check
    description: 'Web application'
  }
];

const Health = () => {
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const checkService = async (service) => {
    if (!service.url) {
      // Self-check for Portal
      return { healthy: true, responseTime: 0 };
    }

    const startTime = Date.now();
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(service.url, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });

      clearTimeout(timeout);
      const responseTime = Date.now() - startTime;

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        return {
          healthy: data.healthy !== false,
          responseTime,
          data
        };
      }

      return {
        healthy: false,
        responseTime,
        error: `HTTP ${response.status}`
      };
    } catch (error) {
      return {
        healthy: false,
        responseTime: Date.now() - startTime,
        error: error.name === 'AbortError' ? 'Timeout' : error.message
      };
    }
  };

  const runHealthChecks = useCallback(async () => {
    setLoading(true);
    const results = {};

    await Promise.all(
      SERVICES.map(async (service) => {
        results[service.key] = await checkService(service);
      })
    );

    setServices(results);
    setLastChecked(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    runHealthChecks();

    // Auto-refresh every 30 seconds
    const interval = setInterval(runHealthChecks, 30000);
    return () => clearInterval(interval);
  }, [runHealthChecks]);

  const getOverallStatus = () => {
    const statuses = Object.values(services);
    if (statuses.length === 0) return 'checking';
    if (statuses.every(s => s.healthy)) return 'healthy';
    if (statuses.some(s => s.healthy)) return 'degraded';
    return 'unhealthy';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#10b981';
      case 'degraded': return '#f59e0b';
      case 'unhealthy': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (healthy) => {
    if (healthy === undefined) return <HelpOutlineIcon sx={{ color: '#6b7280' }} />;
    return healthy
      ? <CheckCircleIcon sx={{ color: '#10b981' }} />
      : <ErrorIcon sx={{ color: '#ef4444' }} />;
  };

  const overallStatus = getOverallStatus();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          borderRadius: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: '#3b82f6',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}
          >
            CARO <span style={{ color: '#64748b', fontWeight: 400 }}>Health</span>
          </Typography>
          <Typography sx={{ color: '#94a3b8', mt: 1 }}>
            System Status Check
          </Typography>
        </Box>

        {/* Overall Status */}
        <Box
          sx={{
            textAlign: 'center',
            py: 3,
            px: 2,
            mb: 3,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#3b82f6' }} />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(overallStatus),
                    boxShadow: `0 0 20px ${getStatusColor(overallStatus)}80`,
                    animation: 'pulse 2s infinite'
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    color: getStatusColor(overallStatus),
                    fontWeight: 600,
                    textTransform: 'capitalize'
                  }}
                >
                  {overallStatus === 'healthy' ? 'All Systems Operational' :
                   overallStatus === 'degraded' ? 'Partial Degradation' :
                   overallStatus === 'unhealthy' ? 'System Issues' : 'Checking...'}
                </Typography>
              </Box>
              {lastChecked && (
                <Typography sx={{ color: '#64748b', fontSize: '0.875rem', mt: 1 }}>
                  Last checked: {lastChecked.toLocaleTimeString()}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Service List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {SERVICES.map((service) => {
            const status = services[service.key];
            return (
              <Box
                key={service.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateX(4px)',
                    borderColor: 'rgba(255, 255, 255, 0.15)'
                  }
                }}
              >
                <Box>
                  <Typography sx={{ color: '#f1f5f9', fontWeight: 500 }}>
                    {service.name}
                  </Typography>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                    {service.description}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {status?.responseTime !== undefined && status.responseTime > 0 && (
                    <Chip
                      label={`${status.responseTime}ms`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: '#94a3b8',
                        fontSize: '0.75rem'
                      }}
                    />
                  )}
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: '#6b7280' }} />
                  ) : (
                    getStatusIcon(status?.healthy)
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* JSON endpoint hint */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>
            JSON endpoint: <code style={{ color: '#94a3b8' }}>/health.json</code>
          </Typography>
        </Box>
      </Paper>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
    </Box>
  );
};

export default Health;
